import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import { PenTool, Send, MapPin, RefreshCw } from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';
import { auth } from '../../lib/firebase';
import { RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Step4Signature({ data, onNext, allData }) {
  const [signature, setSignature] = useState(null);
  const [smsCode, setSmsCode] = useState('');
  const [smsSent, setSmsSent] = useState(false);
  const [smsVerified, setSmsVerified] = useState(false);
  const [sendingSms, setSendingSms] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [testCode, setTestCode] = useState(null);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [smsMethod, setSmsMethod] = useState(null); // 'firebase' or 'backend'
  const [gpsLocation, setGpsLocation] = useState(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsError, setGpsError] = useState(null);
  
  const signatureRef = useRef(null);
  const recaptchaRef = useRef(null);

  useEffect(() => {
    if (data) {
      setSmsVerified(true);
      setGpsLocation(data.gps_location);
    }
  }, [data]);

  // Check permission status first
  const checkPermission = async () => {
    try {
      if (navigator.permissions) {
        const result = await navigator.permissions.query({ name: 'geolocation' });
        return result.state; // 'granted', 'denied', 'prompt'
      }
    } catch (e) {
      // permissions API not supported
    }
    return 'unknown';
  };

  // IP-based geolocation fallback
  const getIpLocation = async () => {
    try {
      const response = await fetch(`${API_URL}/api/geolocation/ip`);
      const data = await response.json();
      if (data.success) {
        return {
          latitude: data.latitude,
          longitude: data.longitude,
          accuracy: data.accuracy || 5000,
          source: 'ip',
          city: data.city,
          country: data.country,
          timestamp: new Date().toISOString()
        };
      }
    } catch (e) {
      console.error('IP geolocation failed:', e);
    }
    return null;
  };

  // Main geolocation function with 3 fallback levels
  const getGpsLocation = async () => {
    setGpsLoading(true);
    setGpsError(null);

    // Check HTTPS
    if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
      setGpsError('La géolocalisation nécessite une connexion HTTPS sécurisée.');
      setGpsLoading(false);
      return;
    }

    // Check if geolocation API exists
    if (!navigator.geolocation) {
      // Try IP fallback directly
      const ipLoc = await getIpLocation();
      if (ipLoc) {
        setGpsLocation(ipLoc);
        setGpsLoading(false);
        toast.success(`Position approximative obtenue (${ipLoc.city || 'IP'})`);
        return;
      }
      setGpsError('Géolocalisation non supportée par ce navigateur.');
      setGpsLoading(false);
      return;
    }

    // Check permission
    const permStatus = await checkPermission();
    if (permStatus === 'denied') {
      // Permission denied - try IP fallback
      const ipLoc = await getIpLocation();
      if (ipLoc) {
        setGpsLocation(ipLoc);
        setGpsLoading(false);
        toast.success(`Position approximative obtenue (${ipLoc.city || 'IP'})`);
        return;
      }
      setGpsError('Accès à la localisation refusé. Autorisez la position dans les réglages de votre navigateur, puis rechargez la page.');
      setGpsLoading(false);
      return;
    }

    // Try GPS with progressive fallback
    const tryGps = (highAccuracy, timeout, maxAge) => {
      return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          (pos) => resolve(pos),
          (err) => reject(err),
          { enableHighAccuracy: highAccuracy, timeout, maximumAge: maxAge }
        );
      });
    };

    const tryWatch = (timeout) => {
      return new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
          navigator.geolocation.clearWatch(watchId);
          reject(new Error('watchPosition timeout'));
        }, timeout);
        
        const watchId = navigator.geolocation.watchPosition(
          (pos) => {
            clearTimeout(timer);
            navigator.geolocation.clearWatch(watchId);
            resolve(pos);
          },
          (err) => {
            clearTimeout(timer);
            navigator.geolocation.clearWatch(watchId);
            reject(err);
          },
          { enableHighAccuracy: false, timeout: timeout, maximumAge: 120000 }
        );
      });
    };

    // Level 1: High accuracy
    try {
      const pos = await tryGps(true, 8000, 0);
      handleGpsSuccess(pos, 'gps_high');
      return;
    } catch (e) {
      console.warn('GPS high accuracy failed:', e.message || e);
    }

    // Level 2: Low accuracy with cached position
    try {
      const pos = await tryGps(false, 12000, 120000);
      handleGpsSuccess(pos, 'gps_low');
      return;
    } catch (e) {
      console.warn('GPS low accuracy failed:', e.message || e);
    }

    // Level 3: watchPosition (sometimes works when getCurrentPosition doesn't)
    try {
      const pos = await tryWatch(15000);
      handleGpsSuccess(pos, 'watch');
      return;
    } catch (e) {
      console.warn('watchPosition failed:', e.message || e);
    }

    // Level 4: IP-based geolocation (always works)
    const ipLoc = await getIpLocation();
    if (ipLoc) {
      setGpsLocation(ipLoc);
      setGpsLoading(false);
      toast.success(`Position approximative obtenue (${ipLoc.city || 'via IP'})`, {
        description: 'Précision réduite (~5km)',
      });
      return;
    }

    // All methods failed
    setGpsError('Impossible d\'obtenir votre position. Vous pouvez continuer sans géolocalisation.');
    setGpsLoading(false);
    toast.error('Géolocalisation indisponible');
  };

  const handleGpsSuccess = (position, source) => {
    const location = {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      accuracy: position.coords.accuracy,
      source: source,
      timestamp: new Date().toISOString()
    };
    setGpsLocation(location);
    setGpsLoading(false);
    toast.success('Position GPS capturée !');
  };

  const clearSignature = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
    }
    setSignature(null);
  };

  const saveSignature = () => {
    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      toast.error('Veuillez signer avant de continuer');
      return;
    }
    
    const signatureData = signatureRef.current.toDataURL();
    setSignature(signatureData);
    toast.success('Signature enregistrée');
  };

  const handleSendSms = async () => {
    const phone = allData.clientInfo?.portable1;
    if (!phone) {
      toast.error('Numéro de téléphone manquant');
      return;
    }

    // Format phone number (add +33 if needed for French numbers)
    let formattedPhone = phone.trim();
    if (formattedPhone.startsWith('0') && formattedPhone.length === 10) {
      formattedPhone = '+33' + formattedPhone.substring(1);
    } else if (!formattedPhone.startsWith('+')) {
      formattedPhone = '+' + formattedPhone;
    }

    setSendingSms(true);
    setTestCode(null);

    // Try Firebase Phone Auth first
    try {
      // Setup invisible reCAPTCHA
      if (!recaptchaRef.current) {
        recaptchaRef.current = new RecaptchaVerifier(auth, 'recaptcha-container', {
          size: 'invisible',
          callback: () => {},
          'expired-callback': () => { recaptchaRef.current = null; }
        });
      }

      const result = await signInWithPhoneNumber(auth, formattedPhone, recaptchaRef.current);
      setConfirmationResult(result);
      setSmsMethod('firebase');
      setSmsSent(true);
      toast.success(`Code SMS envoyé au ${phone}`);
      setSendingSms(false);
      return;
    } catch (firebaseError) {
      console.warn('Firebase SMS failed, falling back to backend:', firebaseError.message);
      // Reset reCAPTCHA on failure
      recaptchaRef.current = null;
    }

    // Fallback to backend Twilio
    try {
      const response = await fetch(`${API_URL}/api/sms/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone_number: formattedPhone }),
      });
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.detail || "Erreur d'envoi SMS");
      }
      
      setSmsMethod('backend');
      setSmsSent(true);
      if (result.test_code) {
        setTestCode(result.test_code);
        toast.success(`Code de validation : ${result.test_code}`, { duration: 15000 });
      } else {
        toast.success(`Code SMS envoyé au ${phone}`);
      }
    } catch (error) {
      console.error('SMS error:', error);
      toast.error(error.message || "Erreur lors de l'envoi du SMS");
    } finally {
      setSendingSms(false);
    }
  };

  const handleVerifySms = async () => {
    if (!smsCode) {
      toast.error('Veuillez entrer le code SMS');
      return;
    }

    setVerifying(true);

    // Firebase verification
    if (smsMethod === 'firebase' && confirmationResult) {
      try {
        await confirmationResult.confirm(smsCode);
        setSmsVerified(true);
        toast.success('Code SMS validé avec succès !');
        setVerifying(false);
        return;
      } catch (error) {
        console.error('Firebase verify error:', error);
        toast.error('Code SMS invalide');
        setVerifying(false);
        return;
      }
    }

    // Backend verification
    const phone = allData.clientInfo?.portable1;
    let formattedPhone = phone?.trim() || '';
    if (formattedPhone.startsWith('0') && formattedPhone.length === 10) {
      formattedPhone = '+33' + formattedPhone.substring(1);
    } else if (!formattedPhone.startsWith('+')) {
      formattedPhone = '+' + formattedPhone;
    }

    try {
      const response = await fetch(`${API_URL}/api/sms/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone_number: formattedPhone, code: smsCode }),
      });
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.detail || 'Erreur de vérification');
      }
      
      if (result.valid) {
        setSmsVerified(true);
        toast.success('Code SMS validé avec succès !');
      } else {
        toast.error('Code SMS invalide');
      }
    } catch (error) {
      console.error('Verification error:', error);
      toast.error(error.message || 'Code SMS invalide');
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!signature) {
      toast.error('Veuillez enregistrer votre signature');
      return;
    }
    
    if (!smsVerified) {
      toast.error('Veuillez vérifier le code SMS');
      return;
    }

    // GPS optionnel mais recommandé
    if (!gpsLocation) {
      const confirmWithoutGPS = window.confirm(
        'La géolocalisation n\'a pas pu être obtenue.\n\n' +
        'Voulez-vous continuer sans localisation GPS ?\n\n' +
        '⚠️ Note: La localisation GPS renforce la validité juridique de la signature.'
      );
      if (!confirmWithoutGPS) {
        return;
      }
    }

    const now = new Date();
    toast.success(gpsLocation ? 'Étape 4 validée - Signature horodatée et géolocalisée' : 'Étape 4 validée - Signature horodatée');
    onNext({
      signature_data: signature,
      sms_code: smsCode,
      sms_verified: true,
      gps_location: gpsLocation || { unavailable: true, reason: 'User continued without GPS' },
      validated_at: now.toISOString(),
      timestamp: now.toLocaleString('fr-FR', { 
        dateStyle: 'full', 
        timeStyle: 'long',
        timeZone: 'Europe/Paris'
      })
    });
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-accent/10 mb-2">
          <PenTool className="w-8 h-8 text-accent" strokeWidth={1.5} />
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Signature électronique
        </h2>
        <p className="text-sm sm:text-base text-foreground/80">
          Signez et validez avec le code SMS
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* GPS Location */}
        <div className="bg-card border border-border rounded-lg p-4 sm:p-6 space-y-4">
          <h3 className="font-semibold text-base sm:text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
            📍 Géolocalisation
          </h3>
          
          {!gpsLocation ? (
            <div className="space-y-4">
              <p className="text-xs sm:text-sm text-foreground/80">
                Pour sécuriser votre signature, nous devons enregistrer votre position géographique.
              </p>
              
              <button
                type="button"
                data-testid="get-gps-btn"
                onClick={getGpsLocation}
                disabled={gpsLoading}
                className="w-full h-12 bg-accent text-accent-foreground rounded-md hover:bg-accent/90 flex items-center justify-center gap-2 disabled:opacity-50 text-sm sm:text-base"
              >
                {gpsLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                    Récupération en cours...
                  </>
                ) : (
                  <>
                    <MapPin className="w-4 h-4" strokeWidth={1.5} />
                    Autoriser la géolocalisation
                  </>
                )}
              </button>
              
              {gpsError && (
                <div className="space-y-3">
                  <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-xs sm:text-sm text-red-700 whitespace-pre-line">{gpsError}</p>
                  </div>
                  <button
                    type="button"
                    onClick={getGpsLocation}
                    disabled={gpsLoading}
                    className="w-full h-10 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 text-sm flex items-center justify-center gap-2"
                  >
                    <RefreshCw className="w-4 h-4" strokeWidth={1.5} />
                    Réessayer
                  </button>
                </div>
              )}

              <div className="text-xs text-foreground/60 bg-blue-50 border border-blue-200 rounded-md p-3">
                <p className="font-medium mb-1">💡 Conseils :</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Activez le GPS/localisation sur votre appareil</li>
                  <li>Autorisez l'accès à la position dans votre navigateur</li>
                  <li>Sur mobile, utilisez une connexion stable</li>
                </ul>
              </div>
            </div>
          ) : (
            <div className="p-3 bg-green-50 border border-green-200 rounded-md">
              <p className="font-medium text-green-700 mb-2 text-sm sm:text-base">Position enregistrée</p>
              <p className="text-xs text-foreground/60 font-mono">
                Lat: {gpsLocation.latitude?.toFixed?.(6) || gpsLocation.latitude}, Long: {gpsLocation.longitude?.toFixed?.(6) || gpsLocation.longitude}
              </p>
              <p className="text-xs text-foreground/60 mt-1">
                Précision: ±{Math.round(gpsLocation.accuracy || 0)}m
                {gpsLocation.source === 'ip' && gpsLocation.city && ` (${gpsLocation.city})`}
              </p>
            </div>
          )}
        </div>

        {/* Signature Pad */}
        <div className="bg-card border border-border rounded-lg p-4 sm:p-6 space-y-4">
          <h3 className="font-semibold text-base sm:text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
            ✍️ Votre signature
          </h3>
          
          <div className="border-2 border-input rounded-lg overflow-hidden bg-white touch-none">
            <SignatureCanvas
              ref={signatureRef}
              canvasProps={{
                className: 'w-full h-40 sm:h-48',
                'data-testid': 'signature-canvas',
                style: { touchAction: 'none' }
              }}
              backgroundColor="white"
            />
          </div>

          <div className="flex gap-3">
            <button
              data-testid="clear-signature-btn"
              type="button"
              onClick={clearSignature}
              className="flex-1 h-10 sm:h-12 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 text-sm sm:text-base"
            >
              Effacer
            </button>
            <button
              data-testid="save-signature-btn"
              type="button"
              onClick={saveSignature}
              className="flex-1 h-10 sm:h-12 bg-accent text-accent-foreground rounded-md hover:bg-accent/90 text-sm sm:text-base"
            >
              Enregistrer
            </button>
          </div>

          {signature && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-700 flex items-center gap-2">
              <PenTool className="w-4 h-4" strokeWidth={1.5} />
              Signature enregistrée
            </div>
          )}
        </div>

        {/* SMS Validation */}
        <div className="bg-card border border-border rounded-lg p-4 sm:p-6 space-y-4">
          <h3 className="font-semibold text-base sm:text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
            📱 Validation par SMS
          </h3>

          {!smsSent ? (
            <div className="space-y-4">
              <p className="text-xs sm:text-sm text-foreground/80">
                Un code de validation sera envoyé au <span className="font-medium font-mono">{allData.clientInfo?.portable1 || 'N/A'}</span>
              </p>
              <button
                data-testid="send-sms-btn"
                type="button"
                onClick={handleSendSms}
                disabled={sendingSms}
                className="w-full h-12 bg-accent text-accent-foreground rounded-md hover:bg-accent/90 flex items-center justify-center gap-2 disabled:opacity-50 text-sm sm:text-base"
              >
                <Send className="w-4 h-4" strokeWidth={1.5} />
                {sendingSms ? 'Envoi...' : 'Envoyer le code SMS'}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                {testCode ? (
                  <>
                    <p className="text-sm font-medium text-green-800 mb-1">
                      Code de validation
                    </p>
                    <p className="text-2xl sm:text-3xl font-bold text-green-900 font-mono tracking-wider">
                      {testCode}
                    </p>
                    <p className="text-xs text-green-700 mt-2">
                      Le SMS n'a pas pu être envoyé - utilisez ce code
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-medium text-green-800">
                      Code SMS envoyé au {allData.clientInfo?.portable1}
                    </p>
                    <p className="text-xs text-green-700 mt-1">
                      Vérifiez vos SMS et entrez le code reçu
                    </p>
                  </>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Code de validation</label>
                <input
                  data-testid="sms-code-input"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={smsCode}
                  onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="Entrez le code"
                  className="w-full h-12 bg-white border border-input rounded-md px-4 text-xl text-center font-mono focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none"
                  maxLength="6"
                  disabled={smsVerified}
                  autoComplete="one-time-code"
                />
              </div>

              {!smsVerified ? (
                <div className="flex gap-3">
                  <button
                    data-testid="verify-sms-btn"
                    type="button"
                    onClick={handleVerifySms}
                    disabled={verifying || !smsCode}
                    className="flex-1 h-12 bg-accent text-accent-foreground rounded-md hover:bg-accent/90 disabled:opacity-50 text-sm sm:text-base"
                  >
                    {verifying ? 'Vérification...' : 'Vérifier'}
                  </button>
                  <button
                    type="button"
                    onClick={handleSendSms}
                    disabled={sendingSms}
                    className="h-12 px-4 sm:px-6 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 disabled:opacity-50 text-sm sm:text-base"
                  >
                    Renvoyer
                  </button>
                </div>
              ) : (
                <div className="p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-700 flex items-center gap-2">
                  <Send className="w-4 h-4" strokeWidth={1.5} />
                  Code SMS validé
                </div>
              )}
            </div>
          )}
        </div>

        {/* Legal Notice */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-xs sm:text-sm text-foreground/80">
          <p className="font-medium mb-2">🔒 Sécurité et conformité eIDAS</p>
          <p>
            Votre signature électronique est sécurisée par validation SMS, géolocalisation GPS et horodatage certifié. 
            Elle a la même valeur juridique qu'une signature manuscrite.
          </p>
        </div>

        {/* Submit Button */}
        <button
          data-testid="next-step-btn"
          type="submit"
          disabled={!signature || !smsVerified}
          className="w-full h-12 sm:h-14 bg-accent text-accent-foreground text-base sm:text-lg font-medium rounded-md hover:bg-accent/90 border-b-2 border-accent-foreground/20 active:border-b-0 active:translate-y-[2px] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Continuer
        </button>
      </form>
      <div id="recaptcha-container"></div>
    </div>
  );
}
